import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { animated, useSpring } from 'react-spring';
import { useMutation } from '@apollo/client';

import {
  createLeadMutation,
  SAVE_DIGGER_ID_MUTATION
} from '../../../../../client/__graphql__/mutations';
import {
  ILeadInput,
  ILeadResponse,
  ISaveDiggerInput,
  ISaveDiggerResponse
} from '../../../../__types__/global';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { BackgroundLoad } from '../../../layout/BackgroundLoad';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { createGTMEvent } from '../../../../utils/tracking/gtm';
import { useProgressiveImage } from '../../../../utils/hooks/useProgressiveImage';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

interface IRoute {
  office: string;
  path: string;
}

const ForventMer: React.FC<RouteComponentProps<IRoute>> = ({
  location,
  match
}) => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const VALUATION_MUTATION = createLeadMutation({
    valuation: {
      response: `{
        success
        data {
          id
          address
          zip
          phone
          email
        }
      }`
    }
  });

  const [lead, { error }] = useMutation<ILeadResponse, ILeadInput>(
    VALUATION_MUTATION,
    {
      onCompleted: ({ lead }) => {
        setLoading(false);
        if (lead.valuation?.success) {
          window.scroll(0, 0);
          setPosted(true);
          // GTM tracking

          createGTMEvent({
            event: 'pmFormSubmission',
            gaEvent: 'Kampanje - Utløsende faktor',
            gaCategory: 'Form',
            gaAction: 'Submit'
          });
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);

      setLoading(true);
      submitWithDigger(
        {
          zip: preparedFields.zip
        },
        ({ diggerId }) => {
          lead({
            variables: {
              input: {
                ...preparedFields,
                diggerId,
                referer: `${location.pathname}${location.search}`,
                leadType: 'VERDIVURDERING',
                pageType: 'GENERELL'
              }
            }
          });
        }
      );
    }
  });

  const EmbedContainer = styled.div`
    position: relative;
    padding: 38.07% 0 0 0;
    margin-top: 4em;
    .VimeoOuterContainer {
      position: absolute;
      top: 0;
      left: 0;
    }

    @media all and (max-width: 375px) {
      padding: 73.07% 0 0 0;
      margin-top: 0px;
    }
  `;

  return (
    <>
      <Seo
        title="Utløsende faktor - PrivatMegleren"
        description="Når det kommer til det mest verdifulle du eier, skal du ha høye forventninger"
        url={location.pathname}
      />

      <EmbedContainer>
        <iframe
          className="VimeoOuterContainer"
          src="https://player.vimeo.com/video/588725735?amp;loop=0&background=1&autoplay=1&muted=1&controls=0"
          frameBorder="0"
          width="100%"
          height="100%"
        ></iframe>
      </EmbedContainer>

      <Container style={pageFadeIn}>
        <FormWrapper style={fadeOut}>
          <Section
            className="form-section"
            style={{ paddingTop: '0', paddingBottom: '10px' }}
          >
            <Heading tag="h2" center={true}>
              Den utløsende faktor
            </Heading>

            <Paragraph center={true} className="form-text">
              Visste du at halvparten av alle som kjøper eiendom ikke hadde
              planer om det? Vår annonseteknologi sørger for suveren
              treffsikkerhet, og gjør at din eiendom når ut til alle potensielle
              kjøpere. Både de som leter, og de som ikke gjør det.
            </Paragraph>
            <Heading tag="h3" center={true}>
              Velg PrivatMegleren og se brikkene falle på plass.
            </Heading>
            <Paragraph center={true} className="form-text">
              Fyll ut skjema for å bli kontaktet av en av våre eiendomsmeglere.
            </Paragraph>
          </Section>
          <Form
            onSubmit={handleSubmit}
            noValidate
            style={{ paddingBottom: '50px' }}
          >
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item, index) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Kontakt meg
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg så snart som mulig.
            </Paragraph>
            <AccordionGeneral />
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default ForventMer;

const FormWrapper = styled(animated.div)``;

const Top: React.FC<{ url: string }> = ({ url }) => {
  const { loaded } = useProgressiveImage({
    url
  });
  const animatedProps = useSpring({
    from: { opacity: 0 },
    to: { opacity: loaded ? 1 : 0 }
  });

  return (
    <StyledTop style={animatedProps}>
      <img src={url} alt="bilde" />
    </StyledTop>
  );
};

const StyledTop = styled(animated.div)`
  margin-top: 66px;
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
    object-position: center;
  }

  @media all and (max-width: 1280px) {
    img {
      min-height: 300px;
      object-fit: cover;
    }
  }
`;
